import { fmtMoney } from "../lib/api";

function safeImgSrc(src) {
  if (!src || typeof src !== 'string') return null;
  if (src.startsWith('https://') || src.startsWith('/api/files/')) return src;
  return null; // bloquer data:, javascript:, http:, chemins relatifs inconnus
}

export function videoEmbedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  const loom = url.match(/loom\.com\/share\/([\w]+)/);
  if (loom) return `https://www.loom.com/embed/${loom[1]}`;
  return null;
}

function SectionHeading({ children }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <span className="w-2.5 h-2.5 bg-primary rotate-45 shrink-0" />
      <h2 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight">{children}</h2>
    </div>
  );
}

export default function SectionView({ section: s, currency = "EUR", clientName, brand }) {
  const items = s.items || [];

  if (s.type === "cover") {
    return (
      <div className="text-center py-8 border-b border-border">
        <p className="text-xs tracking-[0.25em] uppercase font-semibold text-primary mb-4">Proposition commerciale</p>
        <h1 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight leading-none">{s.title}</h1>
        {s.content && <p className="text-muted-foreground mt-5 max-w-xl mx-auto text-lg leading-relaxed">{s.content}</p>}
        {clientName && <p className="text-sm text-muted-foreground mt-6">Préparée pour <span className="font-medium text-foreground">{clientName}</span></p>}
      </div>
    );
  }

  if (s.type === "text") {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <p className="text-foreground/80 leading-relaxed whitespace-pre-line text-[15px]">{s.content}</p>
      </div>
    );
  }

  if (s.type === "goals" || s.type === "process") {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <div className="space-y-6">
          {items.map((it, i) => (
            <div key={it.id || i} className="flex gap-5">
              <span className="font-heading text-2xl font-bold text-primary/30 tabular-nums shrink-0 w-10">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="font-heading text-lg font-semibold">{it.title}</h3>
                {it.content && <p className="text-muted-foreground mt-1 leading-relaxed">{it.content}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (s.type === "stats") {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {items.map((it, i) => (
            <div key={it.id || i} className="border border-border rounded-sm p-6 text-center bg-surface">
              <p className="font-heading text-4xl font-bold text-primary">{it.name}</p>
              <p className="text-sm text-muted-foreground mt-2">{it.description}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (s.type === "team") {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((it, i) => (
            <div key={it.id || i} className="flex items-center gap-4 border border-border rounded-sm p-5">
              {it.image
                ? <img src={safeImgSrc(it.image)} alt={it.name} className="w-16 h-16 rounded-sm object-cover shrink-0" />
                : <div className="w-16 h-16 rounded-sm bg-primary/10 text-primary flex items-center justify-center font-heading font-bold text-xl shrink-0">{(it.name || "?").charAt(0)}</div>}
              <div>
                <p className="font-heading font-semibold">{it.name}</p>
                <p className="text-sm text-muted-foreground">{it.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (s.type === "testimonials") {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((it, i) => (
            <figure key={it.id || i} className="border border-border rounded-sm p-6 bg-surface">
              <blockquote className="text-foreground/90 leading-relaxed">“{it.content}”</blockquote>
              <figcaption className="flex items-center gap-3 mt-5">
                {it.image
                  ? <img src={safeImgSrc(it.image)} alt={it.name} className="w-10 h-10 rounded-full object-cover" />
                  : <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">{(it.name || "?").charAt(0)}</div>}
                <div>
                  <p className="text-sm font-semibold">{it.name}</p>
                  <p className="text-xs text-muted-foreground">{it.description}</p>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    );
  }

  if (s.type === "faq") {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <div className="divide-y divide-border border-t border-border">
          {items.map((it, i) => (
            <div key={it.id || i} className="py-4">
              <p className="font-heading font-semibold">{it.title}</p>
              {it.content && <p className="text-muted-foreground mt-1.5 leading-relaxed">{it.content}</p>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (s.type === "image") {
    return (
      <figure>
        {s.content
          ? <img src={safeImgSrc(s.content)} alt={s.title || ""} className="w-full rounded-sm border border-border object-cover" />
          : <div className="w-full h-48 rounded-sm border border-dashed border-border flex items-center justify-center text-muted-foreground text-sm">Image</div>}
        {s.title && <figcaption className="text-sm text-muted-foreground mt-2 text-center">{s.title}</figcaption>}
      </figure>
    );
  }

  if (s.type === "video") {
    const embed = videoEmbedUrl(s.content);
    return (
      <figure data-testid="video-section">
        {!s.content ? (
          <div className="w-full h-48 rounded-sm border border-dashed border-border flex items-center justify-center text-muted-foreground text-sm">Vidéo</div>
        ) : embed ? (
          <div className="relative w-full rounded-sm overflow-hidden border border-border" style={{ paddingTop: "56.25%" }}>
            <iframe title={s.title || "Vidéo"} src={embed} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen className="absolute inset-0 w-full h-full" />
          </div>
        ) : (
          <video src={safeImgSrc(s.content)} controls className="w-full rounded-sm border border-border" />
        )}
        {s.title && <figcaption className="text-sm text-muted-foreground mt-2 text-center">{s.title}</figcaption>}
      </figure>
    );
  }

  if (s.type === "diagram") {
    return <DiagramSection s={s} brand={brand} />;
  }

  // pricing (default)
  return (
    <div>
      <SectionHeading>{s.title}</SectionHeading>
      <div className="border border-border rounded-sm overflow-hidden">
        {items.map((it, i) => (
          <div key={it.id || i} className={`flex items-center justify-between px-5 py-4 ${i % 2 ? "bg-surface" : "bg-white"}`}>
            <div className="min-w-0">
              <p className="font-medium text-sm">{it.name}</p>
              {it.description && <p className="text-xs text-muted-foreground">{it.description}</p>}
            </div>
            <div className="text-right shrink-0 ml-4">
              <p className="font-mono text-sm font-semibold">{fmtMoney((Number(it.quantity) || 0) * (Number(it.unit_price) || 0), currency)}</p>
              <p className="text-xs text-muted-foreground">{it.quantity} × {fmtMoney(it.unit_price, currency)} {it.recurrence === "mensuel" ? "/mois" : ""}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DiagramSection({ s, brand }) {
  const accent = brand?.accent_hex || '#6366f1';

  if (s.subtype === 'process') {
    const items = Array.isArray(s.items) ? s.items : [];
    return (
      <div>
        <h2 className="text-xl font-bold font-heading mb-6">{s.title}</h2>
        <div className="flex flex-wrap gap-3">
          {items.map((item, idx) => (
            <div key={item.id || idx} className="flex-1 min-w-[160px] max-w-[220px] rounded-sm border border-border p-4 relative">
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold mb-3"
                style={{ backgroundColor: accent }}>
                {item.step || idx + 1}
              </span>
              <p className="text-sm font-semibold leading-tight mb-1">{item.title}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{item.description}</p>
              {idx < items.length - 1 && (
                <span className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-lg">›</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (s.subtype === 'timeline') {
    const items = Array.isArray(s.items) ? s.items : [];
    return (
      <div>
        <h2 className="text-xl font-bold font-heading mb-6">{s.title}</h2>
        <div className="relative pl-6 space-y-6">
          <div className="absolute left-2.5 top-1 bottom-1 w-px bg-border" />
          {items.map((item, idx) => (
            <div key={item.id || idx} className="relative">
              <span className="absolute -left-6 w-5 h-5 rounded-full border-2 flex items-center justify-center"
                style={{
                  backgroundColor: item.milestone ? accent : 'white',
                  borderColor: accent,
                }}>
                {item.milestone && <span className="w-2 h-2 rounded-full bg-white" />}
              </span>
              <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: accent }}>{item.date_label}</p>
              <p className="text-sm font-semibold">{item.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (s.subtype === 'comparison') {
    const data = s.items || {};
    const headers = Array.isArray(data.headers) ? data.headers : [];
    const rows = Array.isArray(data.rows) ? data.rows : [];
    return (
      <div>
        <h2 className="text-xl font-bold font-heading mb-6">{s.title}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="text-left py-2 pr-4 text-muted-foreground font-medium w-1/3"></th>
                {headers.map((h, i) => (
                  <th key={i} className="py-2 px-3 text-center font-semibold rounded-t-sm"
                    style={i === 0 ? { backgroundColor: accent + '18', color: accent } : {}}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className="border-t border-border">
                  <td className="py-2 pr-4 text-muted-foreground text-xs">{row.feature}</td>
                  {(row.values || []).map((val, vi) => (
                    <td key={vi} className="py-2 px-3 text-center"
                      style={vi === 0 ? { color: accent, fontWeight: 600 } : {}}>
                      {val === true || val === 'true' ? '✓' : val === false || val === 'false' ? '—' : val}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return null;
}
